/**
 * @author NikolaV
 */
import { action, computed, makeObservable, observable } from 'mobx';
import { Notify } from '@shared/ui-native/Notify';
import { PageStateService } from '@shared/user-state/PageStateService';
import { LegacyStateService } from '@shared/user-state/legacy/LegacyStateService';
import { alphaNumSort } from '@shared/utils/utils-sort';
import { Ajax2 } from '@shared/Ajax';
import { PageStateUtils } from '@shared/user-state/PageStateUtils';
import { DateUtils } from '@shared/utils/DateUtils';

const emptyFilter: IPageStateFilter = {
    fromTimestamp: null,
    toTimestamp: null,
    datePresetIndex: null,
    filterOnly2d3d: true,
    showMarkersOnMap: false,
};

interface IPageStateFilter extends DateRange.IFilter {
    filterOnly2d3d: boolean;
    showMarkersOnMap: boolean;
}

export class PageStateStore implements ICrudStore<IPageState>, DateRange.IFilterStore, IStateful<Partial<IMapPageStateSavedViewsPanel>> {
    @observable asyncState = {
        isFetching: false,
        fetchingItemUuid: '',
        isCreating: false,
        isUpdating: false,
        isDeleting: false,
        firstFetch: true,
    };

    @observable.shallow items: IPageState[] = [];
    @observable.shallow itemsSimple: IPageState[] = []; // items without data for pins on map

    @observable selectedFolderId = null;
    @observable.shallow activeState: IPageState = null;
    previousState = null;
    @observable sortKey: 'timestamp' | 'name' = 'timestamp';
    @observable loadingStateUuid: string = null;
    @observable focusItemId: number = null;
    @observable is2dMap: boolean = true; // used for filtering out thumbnails of other map
    @observable showAllFolderContent = false; // force disables folder filtering

    @observable public filter: IPageStateFilter = {
        ...emptyFilter,
    };

    @observable filterPinned = false;
    @observable filterOpened = false;

    @computed
    get isDoingAsyncWork() {
        return this.asyncState.isFetching || this.asyncState.isCreating || this.asyncState.isUpdating || this.asyncState.isDeleting || !!this.asyncState.fetchingItemUuid;
    }

    @computed
    get itemsFiltered(): IPageState[] {
        return this.itemsSimple.filter(view => {
            if (this.filter.filterOnly2d3d) {
                const mapType = this.is2dMap ? '2D' : '3D';
                if (view.type !== mapType) {
                    return false;
                }
            }

            if (this.filter.fromTimestamp || this.filter.toTimestamp) {
                const [start, end] = DateUtils.clampDateRange(this.filter.fromTimestamp, this.filter.toTimestamp);
                if (start && view.timestamp < start) return false;
                if (end && view.timestamp > end) return false;
            }

            return true;
        });
    }

    @computed
    get itemsToShowOnMap(): IPageState[] {
        if (!this.filter.showMarkersOnMap) return [];
        return this.itemsFiltered;
    }

    @computed
    get selectedFolderItems(): IPageState[] {
        if (!this.selectedFolderId) return [];

        return (this.showAllFolderContent ? this.itemsSimple : this.itemsFiltered)
            .filter(view => view.folderId === this.selectedFolderId)
            .map(simple => this.items.find(full => full.id === simple.id))
            .filter(Boolean)
            .sort((s1, s2) => {
                if (this.sortKey === 'timestamp') {
                    return alphaNumSort(s2[this.sortKey], s1[this.sortKey]);
                }
                return alphaNumSort(s1[this.sortKey], s2[this.sortKey]);
            });
    }

    @computed
    get numberOfAppliedFilters(): number {
        let count = 0;
        if (this.filter.fromTimestamp || this.filter.toTimestamp) count += 1;
        if (this.filter.filterOnly2d3d) count += 1;
        return count;
    }

    @action.bound
    public clearFilter() {
        this.filter.fromTimestamp = null;
        this.filter.toTimestamp = null;
        this.filter.datePresetIndex = null;
        this.filter.filterOnly2d3d = false;
    }

    @computed
    get counts() {
        const filteredFolderCounts = _.countBy(this.itemsFiltered, f => f.folderId);
        const allFolderCounts = _.countBy(this.itemsSimple, f => f.folderId);

        return {
            filteredFolderCounts,
            allFolderCounts,
        };
    }

    public page: IPageStatePage = null;

    constructor(page: IPageStatePage) {
        makeObservable(this);
        this.page = page;
    }

    setSelectedFolderId = action((folderId: number, focusId?: number) => {
        this.focusItemId = focusId;

        if (folderId === this.selectedFolderId) return;
        this.selectedFolderId = folderId;
        this.showAllFolderContent = false;
        this.fetch(this.selectedFolderId);
    });

    fetchItemsSimple = () => {
        Ajax2({
            url: kuura.getContextPath() + '/api/saved-views',
            data: {
                projectId: kuura.getProjectId(),
                fetchData: false,
                fetchThumbnail: false,
                fetchCenter: true,
            },
        }).then(resp => {
            this.itemsSimple = resp.pageStates;
        });
    };

    refresh = () => {
        this.fetch(this.selectedFolderId);
        this.fetchItemsSimple();
    };

    @action.bound
    create(item: Partial<IPageState>): Promise<IPageState> {
        this.asyncState.isCreating = true;

        const promise = PageStateService.create(item as IPageState);
        promise
            .then(resp => {
                const newState: IPageState = {
                    ...resp,
                    ...item,
                    timestamp: Date.now(),
                };
                if (newState.visibility !== 'HIDDEN' && this.selectedFolderId === newState.folderId) {
                    this.items.unshift(newState);
                }
                this.fetchItemsSimple();
                return newState;
            })
            .finally(() => {
                this.asyncState.isCreating = false;
            });
        return promise;
    }

    @action.bound
    update(item: Partial<IPageState>) {
        this.asyncState.isUpdating = true;
        return PageStateService.update(item.uuid, item)
            .then(resp => {
                this.refresh();
                Notify.success();
            })
            .finally(() => {
                this.asyncState.isUpdating = false;
            });
    }

    @action.bound
    async fetch(folderId: number) {
        if (!folderId) return;
        this.asyncState.isFetching = true;

        const request = PageStateService.fetch(folderId, this.page);
        const legacyRequest = folderId === kuura.getRootFolderId() ? LegacyStateService.fetchAll() : Promise.resolve([]);

        try {
            const [items, legacyItems] = await Promise.all([request, legacyRequest]);

            this.items = [...items, ...legacyItems.reverse()];
        } catch {
            if (!this.asyncState.firstFetch) {
                Notify.warn(kuura.getMessage('general.validation.access_not_allowed'));
            }
        }

        this.asyncState.firstFetch = false;
        this.asyncState.isFetching = false;
    }

    @action.bound
    fetchItem(uuid: string | number) {
        if (typeof uuid === 'string') {
            this.asyncState.fetchingItemUuid = uuid;
        }

        return PageStateService.fetchItem(uuid)
            .catch(Notify.error)
            .finally(() => (this.asyncState.fetchingItemUuid = ''));
    }

    @action.bound
    remove(itemId: number) {
        this.asyncState.isDeleting = true;
        const state = this.items.find(i => i.id == itemId);

        return PageStateService.remove(state)
            .done(resp => {
                this.items.splice(
                    this.items.findIndex(item => item.id === itemId),
                    1
                );
                this.fetchItemsSimple();
                Notify.success();
            })
            .always(() => {
                this.asyncState.isDeleting = false;
            });
    }

    @action.bound
    public setDates(start: number, end: number, presetIndex: number) {
        const [safeStart, safeEnd] = DateUtils.clampDateRange(start, end);
        this.filter.fromTimestamp = safeStart;
        this.filter.toTimestamp = safeEnd;
        this.filter.datePresetIndex = presetIndex;
    }

    stateful = {
        queuedRestoreState: null,

        defaultState: {
            timestamp: {
                from: null,
                to: null,
                preset: null,
            },
            selectedFolderId: null,
            sortKey: 'timestamp',
            filterOnly2d3d: false,
            showMarkersOnMap: false, // explicitly requested to be false
        },

        getCurrentState: (): IMapPageStateSavedViewsPanel => {
            return {
                timestamp: {
                    from: this.filter.fromTimestamp,
                    to: this.filter.toTimestamp,
                    preset: this.filter.datePresetIndex,
                },
                selectedFolderId: this.selectedFolderId || null,
                sortKey: this.sortKey,
                filterOnly2d3d: this.filter.filterOnly2d3d,
                showMarkersOnMap: this.filter.showMarkersOnMap,
            };
        },

        restoreDefaultState: () => {
            this.stateful.restoreState(this.stateful.defaultState, true);
        },

        restoreState: (state: IMapPageStateSavedViewsPanel, liveState?: boolean): void => {
            if (!state) return null;

            if (liveState) {
                if (PageStateUtils.stateHasKey<IMapPageStateDocumentsImagesPanel>(state, 'timestamp')) {
                    if (state.timestamp.hasOwnProperty('preset') && state.timestamp.preset >= 0 && DateUtils.presets[state.timestamp.preset]) {
                        const preset = DateUtils.presets[state.timestamp.preset];
                        this.setDates(preset.from, preset.to, state.timestamp.preset);
                    } else {
                        this.setDates(state.timestamp.from, state.timestamp.to, null);
                    }
                }

                if (PageStateUtils.stateHasKey<IMapPageStateSavedViewsPanel>(state, 'showMarkersOnMap')) {
                    this.filter.showMarkersOnMap = state.showMarkersOnMap as any;
                }

                if (PageStateUtils.stateHasKey<IMapPageStateSavedViewsPanel>(state, 'sortKey')) {
                    this.sortKey = state.sortKey as any;
                }

                if (liveState && PageStateUtils.stateHasKey<IMapPageStateSavedViewsPanel>(state, 'filterOnly2d3d')) {
                    this.filter.filterOnly2d3d = state.filterOnly2d3d as any;
                }
            }
        },
    };
}
